// src/pages/CertificatesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { certificatesAPI, warehousesAPI } from '../services/api';
import { Card, Badge, SectionHeader, EmptyState, Button, Modal, Input, Select, ConfirmModal } from '../components/ui';
import toast from 'react-hot-toast';
import { Wifi, Plus, ShieldCheck, Copy, AlertTriangle, Download, Terminal } from 'lucide-react';
import { format } from 'date-fns';
import styles from './CertificatesPage.module.css';

export default function CertificatesPage() {
    const { companyId, isGaafAdmin } = useAuth();
    const [certs, setCerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showProvision, setShowProvision] = useState(false);
    const [provisionResult, setProvisionResult] = useState(null);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [revokeLoading, setRevokeLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await certificatesAPI.list(isGaafAdmin ? undefined : companyId);
            setCerts(res.data.certificates || []);
        } catch (_) { }
        setLoading(false);
    }, [companyId, isGaafAdmin]);

    useEffect(() => { load(); }, [load]);

    const handleRevoke = async () => {
        if (!revokeTarget) return;
        setRevokeLoading(true);
        try {
            await certificatesAPI.revoke(revokeTarget.certificateId);
            toast.success('Certificate Revoked');
            setRevokeTarget(null);
            load();
        } catch (err) {
            toast.error('Failed to Revoke Certificate');
        }
        setRevokeLoading(false);
    };

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Reader Certificates"
                description="Provision IoT Certificates for RFID Readers — One per Company+Warehouse"
                actions={
                    <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowProvision(true)}>
                        Provision Certificate
                    </Button>
                }
            />
            {loading ? (
                <div className={styles.list}>{[1, 2].map(i => <div key={i} className={styles.skeleton} />)}</div>
            ) : certs.length === 0 ? (
                <EmptyState
                    icon={<ShieldCheck size={40} />}
                    title="No Certificates Provisioned"
                    description="Provision an X.509 Certificate for each Reader. Each Warehouse uses its own Certificate for Security Isolation."
                    action={<Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowProvision(true)}>Provision First Certificate</Button>}
                />
            ) : (
                <div className={styles.list}>
                    {certs.map(cert => (
                        <CertCard key={cert.certificateId} cert={cert} onRevoke={() => setRevokeTarget(cert)} />
                    ))}
                </div>
            )}
            <ConfirmModal
                open={!!revokeTarget}
                onClose={() => setRevokeTarget(null)}
                onConfirm={handleRevoke}
                loading={revokeLoading}
                title="Revoke Certificate"
                message={`Revoke Certificate for Reader "${revokeTarget?.readerId}"? The Reader will immediately lose connectivity and cannot send tag reads until a new certificate is provisioned.`}
                confirmLabel="Revoke"
                confirmVariant="danger"
            />
            <ProvisionModal
                open={showProvision}
                companyId={companyId}
                onClose={() => setShowProvision(false)}
                onProvisioned={(result) => { setShowProvision(false); setProvisionResult(result); load(); }}
            />
            <CredentialsModal
                result={provisionResult}
                open={!!provisionResult}
                onClose={() => setProvisionResult(null)}
            />
        </div>
    );
}

function generateBridgePy(cfg) {
    return `#!/usr/bin/env python3
# GAAFrfid Bridge — generated ${new Date().toLocaleDateString()}

import time, logging, threading, socket, requests
from sllurp import llrp
from http.server import HTTPServer, BaseHTTPRequestHandler

READER_IP         = "${cfg.readerIp}"
READER_PORT       = 5084
API_URL           = "${cfg.gatewayUrl}"
STATUS_URL        = "${cfg.statusUrl}"
GATEWAY_KEY       = "${cfg.gatewayKey}"
COMPANY_ID        = "${cfg.companyId}"
WAREHOUSE_ID      = "${cfg.warehouseId}"
READER_NAME       = "${cfg.readerId}"
PRINTER_IP        = "${cfg.printerIp}"
PRINTER_PORT      = 9100
PRINT_SERVER_PORT = 9200
ANTENNAS          = [1, 2, 3, 4]
BATCH_INTERVAL    = 3.0

logging.basicConfig(level=logging.INFO, format="%(asctime)s [%(levelname)s] %(message)s")
log = logging.getLogger("gaafrfid-bridge")
_pending_reads = []
_lock = threading.Lock()

class PrintHandler(BaseHTTPRequestHandler):
    def log_message(self, format, *args): pass
    def do_POST(self):
        if self.path == "/print":
            length = int(self.headers.get("Content-Length", 0))
            zpl = self.rfile.read(length)
            try:
                with socket.create_connection((PRINTER_IP, PRINTER_PORT), timeout=10) as s:
                    s.sendall(zpl)
                log.info("Printed %d bytes -> %s:%s", len(zpl), PRINTER_IP, PRINTER_PORT)
                self.send_response(200)
                self.send_header("Content-Type", "application/json")
                self.send_header("Access-Control-Allow-Origin", "*")
                self.end_headers()
                self.wfile.write(b'{"ok":true}')
            except Exception as e:
                log.error("Print failed: %s", e)
                self.send_response(500)
                self.send_header("Content-Type", "application/json")
                self.send_header("Access-Control-Allow-Origin", "*")
                self.end_headers()
                self.wfile.write(('{"ok":false,"error":"' + str(e) + '"}').encode())
        else:
            self.send_response(404); self.end_headers()
    def do_OPTIONS(self):
        self.send_response(200)
        self.send_header("Access-Control-Allow-Origin", "*")
        self.send_header("Access-Control-Allow-Methods", "POST, OPTIONS")
        self.send_header("Access-Control-Allow-Headers", "Content-Type")
        self.end_headers()

def print_server_thread():
    server = HTTPServer(("0.0.0.0", PRINT_SERVER_PORT), PrintHandler)
    log.info("Print server on port %s -> printer %s:%s", PRINT_SERVER_PORT, PRINTER_IP, PRINTER_PORT)
    server.serve_forever()

def normalize_epc(epc):
    return epc

def on_tag_report(reader, tags):
    if not tags: return
    now_ms = int(time.time() * 1000)
    tag_list = tags if isinstance(tags, list) else [tags]
    with _lock:
        for tag in tag_list:
            epc_raw = tag.get("EPC-96") or tag.get("EPC") or b""
            if isinstance(epc_raw, bytes):
                try:
                    ascii_val = epc_raw.decode("ascii")
                    if all(c in "0123456789abcdefABCDEF" for c in ascii_val):
                        epc = ascii_val.upper()
                    else:
                        epc = epc_raw.hex().upper()
                except (UnicodeDecodeError, AttributeError):
                    epc = epc_raw.hex().upper()
            else:
                epc = str(epc_raw).upper()
            if not epc: continue
            epc = normalize_epc(epc)
            antenna    = tag.get("AntennaID", 0)
            rssi       = tag.get("PeakRSSI", -70)
            first_seen = tag.get("FirstSeenTimestampUTC", now_ms * 1000)
            last_seen  = tag.get("LastSeenTimestampUTC",  now_ms * 1000)
            _pending_reads.append({
                "epc": epc, "antenna_port": antenna, "peak_rssi_dbm": rssi,
                "first_seen_timestamp": first_seen // 1000,
                "last_seen_timestamp":  last_seen  // 1000,
            })
            log.info("Tag: %s  antenna=%s  rssi=%s dBm", epc, antenna, rssi)

def flush_to_api():
    global _pending_reads
    with _lock:
        if not _pending_reads: return
        batch = _pending_reads[:]; _pending_reads = []
    payload = {"companyId": COMPANY_ID, "warehouseId": WAREHOUSE_ID,
               "hostname": READER_NAME, "type": "tagInventory",
               "timestamp": time.strftime("%Y-%m-%dT%H:%M:%SZ", time.gmtime()),
               "tag_reads": batch}
    try:
        resp = requests.post(API_URL, json=payload,
            headers={"Content-Type": "application/json", "X-Gateway-Api-Key": GATEWAY_KEY}, timeout=10)
        if resp.status_code == 200: log.info("Sent %d tag read(s) -> HTTP %s", len(batch), resp.status_code)
        else:
            log.warning("API %s: %s", resp.status_code, resp.text[:200])
            with _lock: _pending_reads = batch + _pending_reads
    except Exception as e:
        log.error("POST failed: %s", e)
        with _lock: _pending_reads = batch + _pending_reads

def batch_sender_thread():
    while True:
        time.sleep(BATCH_INTERVAL); flush_to_api()

def heartbeat_thread():
    while True:
        try:
            requests.post(STATUS_URL, json={
                "companyId": COMPANY_ID, "warehouseId": WAREHOUSE_ID,
                "readerId": READER_NAME, "status": "online", "firmware": "6.4.1.240",
                "antennaStatus": {str(p): "connected" for p in ANTENNAS},
            }, headers={"Content-Type": "application/json", "X-Gateway-Api-Key": GATEWAY_KEY}, timeout=5)
            log.info("Heartbeat sent")
        except Exception as e: log.warning("Heartbeat failed: %s", e)
        time.sleep(60)

RECONCILE_URL   = "${cfg.reconcileUrl}"
RECONCILE_SWEEPS = 3

def reconcile_on_startup(reader):
    sweep_results = []
    for sweep in range(1, RECONCILE_SWEEPS + 1):
        visible = set()
        def capture(r, tags):
            for tag in (tags if isinstance(tags, list) else [tags]):
                epc_raw = tag.get("EPC-96") or tag.get("EPC") or b""
                if isinstance(epc_raw, bytes):
                    try:
                        ascii_val = epc_raw.decode("ascii")
                        epc = ascii_val.upper() if all(c in "0123456789abcdefABCDEF" for c in ascii_val) else epc_raw.hex().upper()
                    except: epc = epc_raw.hex().upper()
                else: epc = str(epc_raw).upper()
                if epc: visible.add(normalize_epc(epc))
        reader.add_tag_report_callback(capture)
        time.sleep(3)
        reader.remove_tag_report_callback(capture)
        try:
            resp = requests.post(
                RECONCILE_URL,
                json={"companyId": COMPANY_ID, "warehouseId": WAREHOUSE_ID,
                      "hostname": READER_NAME, "visible_epcs": list(visible),
                      "sweep_number": sweep, "total_sweeps": RECONCILE_SWEEPS},
                headers={"Content-Type": "application/json", "X-Gateway-Api-Key": GATEWAY_KEY},
                timeout=15
            )
            if resp.status_code == 200:
                data = resp.json()
                if data.get("departed") or data.get("entered"):
                    log.info("Reconcile sweep %d/%d — departed: %d, entered: %d",
                        sweep, RECONCILE_SWEEPS,
                        len(data.get("departed", [])), len(data.get("entered", [])))
        except Exception as e:
            log.warning("Reconcile sweep %d failed: %s", sweep, e)

def main():
    log.info("GAAFrfid LLRP Bridge  |  Reader: %s  |  Printer: %s  |  Warehouse: %s", READER_IP, PRINTER_IP, WAREHOUSE_ID)
    threading.Thread(target=batch_sender_thread, daemon=True).start()
    threading.Thread(target=heartbeat_thread,    daemon=True).start()
    threading.Thread(target=print_server_thread, daemon=True).start()
    while True:
        reader = None
        try:
            config = llrp.LLRPReaderConfig({
                "antennas": ANTENNAS,
                "tx_power": 0,  # 0 = max power in sllurp
                "session": 1,
                "mode_identifier": None,
                "report_every_n_tags": 1,
                "report_timeout_ms": 500,
                "tag_content_selector": {
                    "EnableROSpecID": False, "EnableSpecIndex": False,
                    "EnableInventoryParameterSpecID": False, "EnableAntennaID": True,
                    "EnableChannelIndex": False, "EnablePeakRSSI": True,
                    "EnableFirstSeenTimestamp": True, "EnableLastSeenTimestamp": True,
                    "EnableTagSeenCount": True, "EnableAccessSpecID": False,
                }})
            reader = llrp.LLRPReaderClient(READER_IP, READER_PORT, config)
            reader.add_tag_report_callback(on_tag_report)
            reader.connect()
            log.info("Connected to R420. Walk a tag through the antenna zone to test.")
            reconcile_on_startup(reader)
            reader.join()
            log.warning("Reader disconnected. Reconnecting in 5s...")
        except KeyboardInterrupt:
            log.info("Shutting down.")
            if reader:
                try: reader.disconnect()
                except: pass
            break
        except Exception as e:
            log.error("Error: %s", e); log.info("Retrying in 5 seconds...")
        time.sleep(5)

if __name__ == "__main__":
    main()
`;
}

function generateRunBat(cfg) {
    return `@echo off
setlocal enabledelayedexpansion

title GAAFrfid Bridge Setup

net session >nul 2>&1
if %errorlevel% neq 0 (
    echo ERROR: Right-click and choose "Run as Administrator"
    pause
    exit /b 1
)

python --version >nul 2>&1
if %errorlevel% neq 0 (
    echo ERROR: Python not found. Install from https://python.org
    pause
    exit /b 1
)

echo Installing Python packages...
python -m pip install sllurp requests --quiet
if %errorlevel% neq 0 (
    echo ERROR: pip install failed.
    pause
    exit /b 1
)

set "DEST=C:\\ProgramData\\gaafrfid-bridge"
if not exist "%DEST%" mkdir "%DEST%"

copy /Y "%~dp0bridge.py" "%DEST%\\bridge.py" >nul
if %errorlevel% neq 0 (
    echo ERROR: Could not copy bridge.py to %DEST%
    pause
    exit /b 1
)

REM Get Python path
python -c "import sys; open('C:\\\\ProgramData\\\\gaafrfid-bridge\\\\pypath.txt','w').write(sys.executable)"
set /p PYEXE=<"%DEST%\\pypath.txt"
del "%DEST%\\pypath.txt" >nul 2>&1

(
    echo @echo off
    echo "%PYEXE%" "%DEST%\\bridge.py"
) > "%DEST%\\start.cmd"

schtasks /delete /tn "GAAFrfidBridge" /f >nul 2>&1
schtasks /create /tn "GAAFrfidBridge" /tr "%DEST%\\start.cmd" /sc ONSTART /ru SYSTEM /rl HIGHEST /f /delay 0001:00
if %errorlevel% neq 0 (
    echo ERROR: Failed to create scheduled task.
    pause
    exit /b 1
)

schtasks /run /tn "GAAFrfidBridge" >nul 2>&1

echo.
echo  ================================================
echo   Setup complete.
echo   Bridge installed to: %DEST%
echo   Task GAAFrfidBridge runs on every boot.
echo   Print server: port 9200 -> printer ${cfg.printerIp}:9100
echo  ================================================
echo.
del /F /Q "%~f0" >nul 2>&1
pause
`;
}

function downloadFile(content, filename) {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}

function CertCard({ cert, onRevoke }) {
    const isRevoked = cert.status === 'revoked';
    const [showBridge, setShowBridge] = useState(false);

    return (
        <Card className={styles.certCard}>
            <div className={styles.certHeader}>
                <div className={styles.certIcon}><ShieldCheck size={18} /></div>
                <div className={styles.certInfo}>
                    <div className={styles.certTitle}>{cert.description || cert.readerId}</div>
                    <div className={styles.certSub}>
                        <code className={styles.certId}>{cert.certificateId?.slice(0, 16)}…</code>
                    </div>
                </div>
                <Badge variant={isRevoked ? 'danger' : 'success'}>{cert.status}</Badge>
            </div>
            <div className={styles.certDetails}>
                <div className={styles.certDetail}>
                    <span className={styles.cdLabel}>Reader ID</span>
                    <code className={styles.cdValue}>{cert.readerId}</code>
                </div>
                <div className={styles.certDetail}>
                    <span className={styles.cdLabel}>Warehouse</span>
                    <code className={styles.cdValue}>{cert.warehouseId}</code>
                </div>
                <div className={styles.certDetail}>
                    <span className={styles.cdLabel}>MQTT Topic</span>
                    <code className={styles.cdValue} style={{ fontSize: '0.7rem' }}>{cert.mqttTopicBase}/tags</code>
                </div>
                <div className={styles.certDetail}>
                    <span className={styles.cdLabel}>Created</span>
                    <span className={styles.cdValue}>{cert.createdAt ? format(new Date(cert.createdAt), 'MM/dd/yyyy') : '—'}</span>
                </div>
            </div>
            {!isRevoked && (
                <div className={styles.certActions}>
                    <div className={styles.httpConfig}>
                        <Wifi size={13} />
                        <span>HTTP Gateway: POST <code>/gateway/tags</code></span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button variant="secondary" size="sm" icon={<Terminal size={13} />} onClick={() => setShowBridge(true)}>
                            Download Bridge
                        </Button>
                        <Button variant="danger" size="sm" icon={<AlertTriangle size={13} />} onClick={onRevoke}>
                            Revoke
                        </Button>
                    </div>
                </div>
            )}
            <BridgeModal open={showBridge} cert={cert} onClose={() => setShowBridge(false)} />
        </Card>
    );
}

function BridgeModal({ open, cert, onClose }) {
    const [readerIp, setReaderIp] = useState('192.168.0.103');
    const [printerIp, setPrinterIp] = useState('192.168.0.104');
    const [gatewayKey, setGatewayKey] = useState('');
    const [downloaded, setDownloaded] = useState(false);

    useEffect(() => {
        if (open) { setDownloaded(false); }
        else { setGatewayKey(''); setDownloaded(false); }
    }, [open]);

    const cfg = {
        readerIp, printerIp, gatewayKey,
        gatewayUrl: cert?.httpConfig?.gatewayUrl || 'https://fgpg84zy01.execute-api.us-east-1.amazonaws.com/gateway/tags',
        statusUrl: cert?.httpConfig?.statusUrl || 'https://fgpg84zy01.execute-api.us-east-1.amazonaws.com/gateway/status',
        reconcileUrl: (cert?.httpConfig?.gatewayUrl || 'https://fgpg84zy01.execute-api.us-east-1.amazonaws.com/gateway/tags').replace('/gateway/tags', '/gateway/reconcile'),
        companyId: cert?.companyId || cert?.httpConfig?.companyId || '',
        warehouseId: cert?.warehouseId || cert?.httpConfig?.warehouseId || '',
        readerId: cert?.readerId || '',
    };

    const handleDownload = () => {
        if (!gatewayKey.trim()) return alert('Paste your Gateway API Key before downloading.');
        if (!printerIp.trim()) return alert('Enter the Printer IP address.');
        downloadFile(generateBridgePy(cfg), 'bridge.py');
        downloadFile(generateRunBat(cfg), 'run_bridge.bat');
        setDownloaded(true);
        toast.success('bridge.py and run_bridge.bat downloaded');
    };

    return (
        <Modal open={open} onClose={onClose} title="Download LLRP Bridge Script" size="md">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className={styles.infoBox}>
                    <Terminal size={16} />
                    <p>Downloads a pre-filled <code>bridge.py</code> + <code>run_bridge.bat</code>. Right-click <code>run_bridge.bat</code> → <strong>Run as Administrator</strong>. Also starts a <strong>print server on port 9200</strong> so the dashboard can print labels directly.</p>
                </div>
                <Input
                    label="Reader IP Address *"
                    value={readerIp}
                    onChange={e => setReaderIp(e.target.value)}
                    placeholder="192.168.0.103"
                    hint="IP of the Impinj R420 — check router DHCP for SpeedwayR-xx-xx-xx"
                />
                <Input
                    label="Printer IP Address *"
                    value={printerIp}
                    onChange={e => setPrinterIp(e.target.value)}
                    placeholder="192.168.0.104"
                    hint="IP of the SATO CT4-LX — check printer touchscreen: HOME → Settings → Information → LAN → LAN IPv4 Address"
                />
                <Input
                    label="Gateway API Key *"
                    value={gatewayKey}
                    onChange={e => setGatewayKey(e.target.value)}
                    placeholder="zx12cv34..."
                    hint="CMD command"
                />
                <div className={styles.bridgeConfigPreview}>
                    <div className={styles.bcp_row}><span>Company ID</span><code>{cfg.companyId}</code></div>
                    <div className={styles.bcp_row}><span>Warehouse ID</span><code>{cfg.warehouseId}</code></div>
                    <div className={styles.bcp_row}><span>Reader ID</span><code>{cfg.readerId}</code></div>
                    <div className={styles.bcp_row}><span>Reader IP</span><code>{cfg.readerIp}</code></div>
                    <div className={styles.bcp_row}><span>Printer IP</span><code>{cfg.printerIp}</code></div>
                    <div className={styles.bcp_row}><span>Print Port</span><code>9200</code></div>
                </div>
                {downloaded && (
                    <div className={styles.successBox}>
                        ✓ Files downloaded. Place both files in the same folder. Right-click <code>run_bridge.bat</code> → <strong>Run as Administrator</strong>.
                    </div>
                )}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" icon={<Download size={14} />} onClick={handleDownload}>
                        Download bridge.py + run_bridge.bat
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function ProvisionModal({ open, companyId, onClose, onProvisioned }) {
    const { isGaafAdmin } = useAuth();
    const [warehouses, setWarehouses] = useState([]);
    const [form, setForm] = useState({ warehouseId: '', readerId: '', description: '' });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        if (open) { setForm({ warehouseId: '', readerId: '', description: '' }); setLoading(false); }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        warehousesAPI.list(companyId).then(res => setWarehouses((res.data.warehouses || []).filter(w => w.status !== 'inactive'))).catch(() => { });
    }, [open, companyId]);

    const handleSubmit = async () => {
        if (!form.warehouseId) return toast.error('Select a Warehouse');
        if (!form.readerId) return toast.error('Enter a Reader ID');
        setLoading(true);
        try {
            const res = await certificatesAPI.provision({ companyId, ...form });
            toast.success('Certificate Provisioned — Save the Private Key NOW!');
            onProvisioned(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Provisioning Failed');
        }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} title="Provision IoT Certificate" size="md">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className={styles.infoBox}>
                    <ShieldCheck size={16} />
                    <p>Each Reader gets a unique X.509 Certificate. The Private Key is shown <strong>ONCE</strong> — Save it IMMEDIATELY.</p>
                </div>
                <Select label="Warehouse *" value={form.warehouseId} onChange={e => set('warehouseId', e.target.value)}>
                    <option value="">Select Warehouse…</option>
                    {warehouses.map(w => <option key={w.warehouseId} value={w.warehouseId}>{w.name}</option>)}
                </Select>
                <Input label="Reader ID *" value={form.readerId} onChange={e => set('readerId', e.target.value)} placeholder="r420-gaaf-001" hint="Unique Identifier for this Reader Device" />
                <Input label="Description" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Main Warehouse Entrance Reader" />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--border-subtle)' }}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" loading={loading} icon={<ShieldCheck size={14} />} onClick={handleSubmit}>Provision</Button>
                </div>
            </div>
        </Modal>
    );
}

function CredentialsModal({ result, open, onClose }) {
    if (!result) return null;
    const { credentials, certificate } = result;

    const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied to Clipboard'); };
    const download = (content, filename) => {
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
    };

    const httpConfigJson = JSON.stringify({
        thingName: certificate.thingName,
        gatewayUrl: credentials.httpConfig?.gatewayUrl,
        statusUrl: credentials.httpConfig?.statusUrl,
        apiKeyHeader: 'X-Gateway-Api-Key',
        apiKeyNote: 'CMD command value',
        companyId: credentials.httpConfig?.companyId,
        warehouseId: credentials.httpConfig?.warehouseId,
    }, null, 2);

    return (
        <Modal open={open} onClose={onClose} title="Certificate Credentials — Save NOW!" size="lg">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className={styles.warningBox}>
                    <AlertTriangle size={16} />
                    <strong>The Private Key is shown ONCE and never stored. Download it NOW.</strong>
                </div>
                <div className={styles.credSection}>
                    <div className={styles.credHeader}><span>Reader Configuration</span></div>
                    <div className={styles.credGrid}>
                        <div className={styles.credItem}>
                            <span className={styles.credLabel}>Thing Name</span>
                            <code className={styles.credValue}>{certificate?.thingName}</code>
                        </div>
                        <div className={styles.credItem}>
                            <span className={styles.credLabel}>Gateway URL</span>
                            <code className={styles.credValue} style={{ fontSize: '0.72rem' }}>{credentials?.httpConfig?.gatewayUrl}</code>
                        </div>
                        <div className={styles.credItem}>
                            <span className={styles.credLabel}>Company ID</span>
                            <code className={styles.credValue}>{credentials?.httpConfig?.companyId}</code>
                        </div>
                        <div className={styles.credItem}>
                            <span className={styles.credLabel}>Warehouse ID</span>
                            <code className={styles.credValue}>{credentials?.httpConfig?.warehouseId}</code>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <Button variant="primary" icon={<Download size={14} />}
                        onClick={() => download(credentials.privateKey, `${certificate.thingName}-private.key`)}>
                        Download Private Key
                    </Button>
                    <Button variant="secondary" icon={<Download size={14} />}
                        onClick={() => download(credentials.certificatePem, `${certificate.thingName}-cert.pem`)}>
                        Download Certificate
                    </Button>
                    <Button variant="secondary" icon={<Copy size={14} />} onClick={() => copy(httpConfigJson)}>
                        Copy HTTP Config
                    </Button>
                </div>
                <Button variant="ghost" onClick={onClose} fullWidth>I have saved the Credentials — Close</Button>
            </div>
        </Modal>
    );
}
